import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Button, Modal, Pagination, Select, SelectItem, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, TextArea } from '@carbon/react';
import { formatVnd } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, EmptyState, ErrorState, InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { JACKPOT_ACTION } from '../components/labels';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';
import type { Command } from './Commands';
import { JackpotRules } from './JackpotConfig';
import { JackpotBot } from './JackpotBot';
import { MoneyInput } from '../components/MoneyInput';

// Jackpots (P1-D09). The game processes hold every pot in memory, so a change is a signed command
// a second admin approves; the process that holds the pot applies it to its memory and its table in
// one step (backend/lib/lenh_hu.js).
type Pending = { id: string; status: string; createdAt: string; payload: { hanh_dong: string; gia?: number; delta?: number } };
type Pot = {
  bang: 'jackpots' | 'jackpots_hea' | 'hu_ban_hea'; ten: string; he: 'A' | 'B'; game: { id: string; name: string } | null;
  value: number; stored: number; fund: number | null; inMemory: boolean; seed: number | null; seedFromCms: boolean; pending: Pending[];
};
type Data = { pots: Pot[]; live: { heA: boolean; heB: boolean } };
type Win = { id: number; he: string; gid: string; game: string; khoa: string; user_id: number | null; nick: string; tien: number; trang_thai: string; at: string | null };
type Wins = { total: number; sum: number; games: { id: string; name: string }[]; items: Win[] };

const KIND: Record<Pot['bang'], string> = { jackpots: 'Mini game và slot hệ B', jackpots_hea: 'Slot hệ A', hu_ban_hea: 'Bàn hệ A' };
const WIN_STATUS: Record<string, string> = { da_tra: 'Đã trả', cho: 'Đang chờ', huy: 'Hủy' };

function describe(p: { hanh_dong: string; gia?: number; delta?: number }) {
  if (p.hanh_dong === 'dat') return `Đặt ${formatVnd(p.gia ?? 0)}`;
  if (p.hanh_dong === 'cong') return `${(p.delta ?? 0) > 0 ? 'Cộng' : 'Trừ'} ${formatVnd(Math.abs(p.delta ?? 0))}`;
  if (p.hanh_dong === 'moc') return `Đổi mốc thành ${formatVnd(p.gia ?? 0)}`;
  return 'Đặt lại về mốc';
}

function ChangeDialog({ pot, onClose }: { pot: Pot; onClose: () => void }) {
  const nav = useNavigate();
  const [action, setAction] = useState('dat');
  const [amount, setAmount] = useState('');
  const [minus, setMinus] = useState(false);
  const [reason, setReason] = useState('');
  // one request key per opening: a double click or a resend never changes the pot twice
  const [key] = useState(() => crypto.randomUUID());
  const n = Number(amount || 0);
  const body = action === 'dat' || action === 'moc' ? { hanh_dong: action, gia: n }
    : action === 'cong' ? { hanh_dong: action, delta: minus ? -n : n } : { hanh_dong: action };
  const after = action === 'dat' ? n : action === 'cong' ? pot.value + (minus ? -n : n) : action === 'dat_lai' ? pot.seed ?? pot.value : pot.value;
  const bad = (action !== 'dat_lai' && !amount) || (action === 'moc' && n < 1) || after < 0 || reason.trim().length < 3;
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>('/api/admin/jackpots/commands', { bang: pot.bang, ten: pot.ten, reason: reason.trim(), ...body, key }),
    onSuccess: (r) => nav('/commands/' + r.command.id),
  });
  return (
    <Modal open modalHeading={`Đổi hũ ${pot.game?.name ?? pot.ten}`} primaryButtonText="Thực hiện ngay" secondaryButtonText="Hủy"
      primaryButtonDisabled={bad || m.isPending} onRequestClose={onClose} onRequestSubmit={() => !bad && m.mutate()}>
      <div className="stack">
        <p className="muted num">{pot.ten} · hệ {pot.he}</p>
        <p>Hiện tại <Money v={pot.value} />{pot.seed != null ? <>, mốc <Money v={pot.seed} /></> : null}</p>
        <Select id="jp-action" labelText="Việc" value={action} onChange={(e) => { setAction(e.target.value); setAmount(''); }}>
          {Object.entries(JACKPOT_ACTION).map(([k, v]) => <SelectItem key={k} value={k} text={v} />)}
        </Select>
        {action === 'cong' ? (
          <Select id="jp-sign" labelText="Chiều" value={minus ? '-' : '+'} onChange={(e) => setMinus(e.target.value === '-')}>
            <SelectItem value="+" text="Cộng vào hũ" />
            <SelectItem value="-" text="Trừ khỏi hũ" />
          </Select>
        ) : null}
        {action !== 'dat_lai' ? (
          <MoneyInput id="jp-amount" labelText={action === 'moc' ? 'Mốc mới (giá trị hũ sau mỗi lần nổ)' : action === 'cong' ? 'Số tiền' : 'Giá trị mới'}
            maxDigits={14} value={amount} onChange={setAmount} />
        ) : null}
        {action !== 'moc' ? (
          <p>Sau khi đổi: <strong><Money v={after} /></strong>{after < 0 ? <span className="num--neg"> (hũ không được âm)</span> : null}</p>
        ) : <p className="muted">Giá trị hũ hiện tại giữ nguyên; lần nổ sau hũ về mốc mới.</p>}
        <TextArea id="jp-reason" labelText="Lý do (bắt buộc)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        <p className="muted">Lệnh thực hiện ngay, không chờ người duyệt. Máy chủ giữ hũ áp dụng ngay, ván đang quay xong trước.</p>
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

function PotList() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['jackpots'], queryFn: () => api.get<Data>('/api/admin/jackpots'), refetchInterval: 15_000 });
  const [pick, setPick] = useState<Pot | null>(null);
  if (q.isLoading) return <BlockSkeleton lines={8} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { pots, live } = q.data;
  const manage = can('jackpots.manage');
  return (
    <div className="stack">
      <div className="row health">
        <span>Hệ A: {live.heA ? 'đang trả lời' : <span className="num--neg">không trả lời, giá trị lấy từ bảng</span>}</span>
        <span>Hệ B: {live.heB ? 'đang trả lời' : <span className="num--neg">không trả lời, giá trị lấy từ bảng</span>}</span>
      </div>
      {(Object.keys(KIND) as Pot['bang'][]).map((bang) => {
        const rows = pots.filter((p) => p.bang === bang);
        return (
          <section key={bang} className="section">
            <h2>{KIND[bang]}</h2>
            {rows.length ? (
              <table className="plain">
                <thead><tr><th>Game</th><th>Mã hũ</th><th className="r">Hũ</th><th className="r">Mốc</th><th className="r">Quỹ dự trữ</th><th>Lệnh chờ</th><th /></tr></thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.ten}>
                      <td className="is-title">{p.game?.name ?? '-'}</td>
                      <td className="num muted" data-label="Mã hũ">{p.ten}{!p.inMemory ? <div><Tag size="sm" type="gray">chưa nạp vào máy chủ</Tag></div> : null}</td>
                      <td className="r" data-label="Hũ"><Money v={p.value} /></td>
                      <td className="r" data-label="Mốc">{p.seed != null ? <><Money v={p.seed} />{p.seedFromCms ? <div><Tag size="sm" type="blue">CMS đặt</Tag></div> : null}</> : '-'}</td>
                      <td className="r" data-label="Quỹ dự trữ">{p.fund != null ? <Money v={p.fund} /> : '-'}</td>
                      <td data-label="Lệnh chờ">{p.pending.map((c) => (
                        <div key={c.id}><Link className="cell-link" to={'/commands/' + c.id}>{describe(c.payload)}</Link> <span className="muted">{c.status === 'AWAITING_APPROVAL' ? 'chờ duyệt (lệnh cũ)' : 'đang gửi'}</span></div>
                      ))}</td>
                      <td>{manage ? <Button kind="ghost" size="sm" disabled={p.pending.length > 0} onClick={() => setPick(p)}>Đổi</Button> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState title="Chưa có hũ" body="Máy chủ tạo hũ khi có người vào game có hũ lần đầu." />}
          </section>
        );
      })}
      {pick ? <ChangeDialog pot={pick} onClose={() => setPick(null)} /> : null}
    </div>
  );
}

const WIN_KEYS = ['he', 'game', 'status', 'player', 'from', 'to'];
function WinList() {
  const f = useUrlFilters(WIN_KEYS, { prefix: 'w_' });
  const q = useQuery({
    queryKey: ['jackpot-wins', f.query, f.page, f.pageSize],
    queryFn: () => api.get<Wins>('/api/admin/jackpots/wins', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
  });
  const fields: FilterField[] = [
    { key: 'game', label: 'Game', kind: 'select', options: [['', 'Mọi game'], ...(q.data?.games ?? []).map((g) => [g.id, g.name] as const)] },
    { key: 'player', label: 'Người chơi', kind: 'text', placeholder: 'Mã, tên đăng nhập hoặc nick' },
    { key: 'he', label: 'Hệ', kind: 'select', options: [['', 'Cả hai'], ['A', 'Hệ A'], ['B', 'Hệ B']] },
    { key: 'status', label: 'Trạng thái', kind: 'select', options: [['', 'Mọi trạng thái'], ...Object.entries(WIN_STATUS)] },
    { key: 'from', label: 'Từ ngày', kind: 'date' },
    { key: 'to', label: 'Đến ngày', kind: 'date' },
  ];
  return (
    <div className="stack">
      <FilterBar id="jw" fields={fields} filters={f} />
      <InlineError error={q.error} />
      {q.data ? <p>{formatVnd(q.data.total)} lần nổ, đã trả <Money v={q.data.sum} /></p> : null}
      {q.data?.items.length ? (
        <table className="plain">
          <thead><tr><th>Lúc</th><th>Game</th><th>Hũ</th><th>Người chơi</th><th className="r">Tiền</th><th>Trạng thái</th></tr></thead>
          <tbody>
            {q.data.items.map((w) => (
              <tr key={w.id}>
                <td><When v={w.at} /></td>
                <td className="is-title">{w.game} <span className="muted">hệ {w.he}</span></td>
                <td className="num muted" data-label="Hũ">{w.khoa}</td>
                <td data-label="Người chơi">{w.user_id != null ? <Link className="cell-link" to={'/users/' + w.user_id}>{w.nick || '#' + w.user_id}</Link> : w.nick}</td>
                <td className="r" data-label="Tiền"><Money v={w.tien} /></td>
                <td data-label="Trạng thái">{WIN_STATUS[w.trang_thai] ?? w.trang_thai}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : q.data ? <EmptyState title="Chưa có lần nổ hũ nào khớp bộ lọc" /> : <BlockSkeleton lines={6} />}
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[20, 50, 100]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} lần nổ`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
    </div>
  );
}

export function JackpotsPage() {
  const [tab, setTab] = useState(0);
  return (
    <div className="page">
      <PageHeader title="Hũ"
        lead={<>Giá trị hũ lấy thẳng từ máy chủ đang giữ hũ, cập nhật mỗi 15 giây. Đặt giá trị, cộng / trừ, đặt lại về mốc hay đổi mốc đều thực hiện ngay, không chờ người duyệt; máy chủ giữ hũ áp dụng vào bộ nhớ và bảng cùng một lúc. Phần cược góp vào hũ, tần suất nổ và luật hũ x88 Baccarat đặt ở tab Luật hũ; bot nổ hũ và bot góp hũ (tốc độ tăng, mức trần) ở tab Bot hũ.</>} />
      <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
        <TabList aria-label="Hũ">
          <Tab>Hũ đang chạy</Tab>
          <Tab>Lịch sử nổ hũ</Tab>
          <Tab>Luật hũ</Tab>
          <Tab>Bot hũ</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>{tab === 0 ? <PotList /> : null}</TabPanel>
          <TabPanel>{tab === 1 ? <WinList /> : null}</TabPanel>
          <TabPanel>{tab === 2 ? <JackpotRules /> : null}</TabPanel>
          <TabPanel>{tab === 3 ? <JackpotBot /> : null}</TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  );
}
